import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const GroupManager = () => {
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('');
  const [emails, setEmails] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/mail/groups`);
      setGroups(res.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch groups.', severity: 'error' });
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleSave = async () => {
    if (!name.trim() || !emails.trim()) {
      setSnackbar({ open: true, message: 'Please fill in all fields.', severity: 'warning' });
      return;
    }

    const emailArray = emails.split(',').map((e) => e.trim()).filter(Boolean);

    try {
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/mail/groups/${editingId}`, {
          name,
          emails: emailArray,
        });
        setSnackbar({ open: true, message: 'Group updated successfully.', severity: 'success' });
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/mail/groups`, {
          name,
          emails: emailArray,
        });
        setSnackbar({ open: true, message: 'Group created successfully.', severity: 'success' });
      }

      setName('');
      setEmails('');
      setEditingId(null);
      fetchGroups();
    } catch {
      setSnackbar({ open: true, message: 'Error saving group.', severity: 'error' });
    }
  };

  const handleEdit = (group) => {
    setName(group.name);
    setEmails(group.emails.join(', '));
    setEditingId(group._id);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/mail/groups/${id}`);
      setSnackbar({ open: true, message: 'Group deleted.', severity: 'success' });
      fetchGroups();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete group.', severity: 'error' });
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Manage Email Groups
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
        <TextField
          label="Group Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <TextField
          label="Emails (comma separated)"
          multiline
          minRows={3}
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          fullWidth
        />

        <Button variant="contained" onClick={handleSave} sx={{ alignSelf: 'center', width: '200px' }}>
          {editingId ? 'Update Group' : 'Save Group'}
        </Button>
      </Box>

      <Typography variant="h6" gutterBottom>
        Existing Groups
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Emails</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group._id}>
                <TableCell>{group.name}</TableCell>
                <TableCell>{group.emails.join(', ')}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleEdit(group)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(group._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No groups found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GroupManager;
